/**
 * Solid bindings for the gesture recognizer.
 *
 * `pointerHandlers()` JSX handlers using Pointer Events + pointer capture
 * (modern; preferred for per-element gestures).
 *
 * Plus `anyFlag()` to read suppression flags from the shared interaction context.
 */

import type { InteractionContext } from "@fleet/ui";
import type { GestureRecognizer } from "./types";

/** True if any of `flags` is currently set in the interaction context. */
export function anyFlag(interaction: InteractionContext, flags: readonly string[]): boolean {
  return flags.some((flag) => interaction.get(flag));
}

interface PointerHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

/**
 * JSX pointer handlers for a single element. Captures the pointer once the
 * gesture engages (so it keeps tracking outside the element) and releases on
 * up/cancel. Bind on an element with an appropriate `touch-action` (e.g.
 * `touch-pan-y` for a horizontal swipe) so the browser yields the right axis.
 */
export function pointerHandlers(
  recognizer: GestureRecognizer,
  options: { capture?: boolean } = {},
): PointerHandlers {
  const capture = options.capture ?? true;
  let capturedId: number | null = null;

  return {
    onPointerDown(e) {
      if (e.pointerType === "mouse" && e.button !== 0) {
        return;
      }
      recognizer.start(e.clientX, e.clientY, { target: e.target });
    },
    onPointerMove(e) {
      const wasEngaged = recognizer.isEngaged();
      recognizer.move(e.clientX, e.clientY, () => {
        e.preventDefault();
      });
      if (capture && !wasEngaged && recognizer.isEngaged() && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
          capturedId = e.pointerId;
        } catch {
          /* capture is best-effort */
        }
      }
    },
    onPointerUp(e) {
      recognizer.end(e.clientX, e.clientY);
      if (capturedId !== null && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.releasePointerCapture(capturedId);
        } catch {
          /* element may already be detached */
        }
        capturedId = null;
      }
    },
    onPointerCancel() {
      recognizer.cancel();
      capturedId = null;
    },
  };
}

/**
 * Attach Touch + Mouse listeners for a recognizer to a window/element and
 * return a cleanup. Framework-agnostic so it can be called from a Solid ref
 * callback (for conditionally-rendered elements like the drawer) as well as the
 * `bindTouchMouse` onMount wrapper. Mirrors the legacy page/drawer adapters
 * exactly so scroll-blocking (`preventDefault` on engaged moves) is preserved.
 */
export function attachTouchMouse(target: EventTarget, recognizer: GestureRecognizer): () => void {
  // Handlers are typed as `EventListener` and narrow via `instanceof` so they
  // bind to a generic EventTarget without unsound `as EventListener` casts.
  const onTouchStart: EventListener = (e) => {
    if (!(e instanceof TouchEvent) || e.touches.length !== 1) {
      return;
    }
    const [touch] = e.touches;
    if (touch) {
      recognizer.start(touch.clientX, touch.clientY, { target: e.target });
    }
  };
  const onTouchMove: EventListener = (e) => {
    if (!(e instanceof TouchEvent) || e.touches.length !== 1) {
      return;
    }
    const [touch] = e.touches;
    if (touch) {
      recognizer.move(touch.clientX, touch.clientY, () => {
        if (e.cancelable) {
          e.preventDefault();
        }
      });
    }
  };
  const onTouchEnd: EventListener = (e) => {
    if (!(e instanceof TouchEvent)) {
      return;
    }
    if (e.changedTouches.length !== 1) {
      recognizer.cancel();
      return;
    }
    const [touch] = e.changedTouches;
    if (touch) {
      recognizer.end(touch.clientX, touch.clientY);
    }
  };
  const onTouchCancel: EventListener = () => {
    recognizer.cancel();
  };

  const onMouseMove: EventListener = (e) => {
    if (e instanceof MouseEvent) {
      recognizer.move(e.clientX, e.clientY, () => {
        e.preventDefault();
      });
    }
  };
  const onMouseUp: EventListener = (e) => {
    if (e instanceof MouseEvent) {
      recognizer.end(e.clientX, e.clientY);
    }
    globalThis.removeEventListener("mousemove", onMouseMove);
    globalThis.removeEventListener("mouseup", onMouseUp);
  };
  const onMouseDown: EventListener = (e) => {
    if (!(e instanceof MouseEvent) || e.button !== 0) {
      return;
    }
    recognizer.start(e.clientX, e.clientY, { target: e.target });
    globalThis.addEventListener("mousemove", onMouseMove);
    globalThis.addEventListener("mouseup", onMouseUp);
  };

  target.addEventListener("touchstart", onTouchStart, { passive: false });
  target.addEventListener("touchmove", onTouchMove, { passive: false });
  target.addEventListener("touchend", onTouchEnd, { passive: false });
  target.addEventListener("touchcancel", onTouchCancel, { passive: false });
  target.addEventListener("mousedown", onMouseDown);

  return () => {
    target.removeEventListener("touchstart", onTouchStart);
    target.removeEventListener("touchmove", onTouchMove);
    target.removeEventListener("touchend", onTouchEnd);
    target.removeEventListener("touchcancel", onTouchCancel);
    target.removeEventListener("mousedown", onMouseDown);
    globalThis.removeEventListener("mousemove", onMouseMove);
    globalThis.removeEventListener("mouseup", onMouseUp);
  };
}
